// Content kit (DES1, prototype v4; STARTERS-SPEC §1): one visual system for the
// numbers, panels, charts and notes that chat answers, artifacts, decks and apps
// show. Presentational only: words, values and tones come from the caller (typed
// data, never parsed text). Chart drawing stays with the chart renderer; this kit
// frames it. Styles live in content.css and use only --pc-* tokens.
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** A titled section of an app or artifact: "Products · Sorted by gap". */
export function Panel({
  title,
  meta,
  actions,
  className,
  children,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section data-slot="content-panel" className={cn('pc-panel', className)}>
      {title || meta || actions ? (
        <header className="pc-panel-head">
          {title ? <h3 className="pc-panel-title">{title}</h3> : null}
          {meta ? <span className="pc-panel-meta">{meta}</span> : null}
          {actions ? <span className="pc-panel-actions">{actions}</span> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export type ComparisonTone = 'good' | 'bad' | 'neutral';

export interface StatComparison {
  text: string;
  /** Whether the change is good for this business, decided by the metric's
   *  definition (a fall in returns is good), never by the sign. */
  tone: ComparisonTone;
}

/** One number. A missing value shows "—" with its reason, never 0. */
export function StatCard({
  label,
  value,
  comparison,
  note,
  missing,
}: {
  label: string;
  value: ReactNode | null;
  comparison?: StatComparison;
  note?: ReactNode;
  /** Shown instead of the value when it is unknown ("No data yet", "As of Thu"). */
  missing?: string;
}) {
  const unknown = value === null || value === undefined;
  return (
    <div data-slot="content-stat" data-missing={unknown || undefined} className="pc-stat">
      <span className="pc-stat-label">{label}</span>
      {unknown ? (
        <MissingValue label={missing ?? ''} />
      ) : (
        <span className="pc-stat-value">{value}</span>
      )}
      {comparison ? (
        <span className="pc-stat-cmp" data-tone={comparison.tone}>
          {comparison.text}
        </span>
      ) : null}
      {note ? <span className="pc-stat-note">{note}</span> : null}
    </div>
  );
}

/** Two to four StatCards: four across on wide frames, one per line on phones. */
export function StatRow({ children }: { children: ReactNode }) {
  return (
    <div data-slot="content-stat-row" className="pc-stat-row">
      {children}
    </div>
  );
}

/** "—" for a value that is unknown, with its reason spoken and shown. */
export function MissingValue({ label }: { label: string }) {
  return (
    <span data-slot="content-missing" className="pc-missing">
      <span className="pc-missing-dash" aria-hidden="true">
        —
      </span>
      {label ? <span className="pc-missing-label">{label}</span> : null}
    </span>
  );
}

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

/** A short note in the flow: a failed source, a gap, what changed. Danger is
 *  announced as an alert; the others are notes. */
export function Callout({
  tone = 'info',
  icon,
  title,
  actions,
  children,
}: {
  tone?: CalloutTone;
  icon?: ReactNode;
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'note'}
      data-slot="content-callout"
      data-tone={tone}
      className="pc-callout"
    >
      {icon ? (
        <span className="pc-callout-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="pc-callout-body">
        {title ? <p className="pc-callout-title">{title}</p> : null}
        {children}
        {actions ? <div className="pc-callout-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/** A section heading in a report, page or document, with optional meta. */
export function SectionHeading({
  level = 2,
  meta,
  children,
}: {
  level?: 2 | 3;
  meta?: ReactNode;
  children: ReactNode;
}) {
  const Tag = level === 2 ? 'h2' : 'h3';
  return (
    <div data-slot="content-heading" data-level={level} className="pc-heading">
      <Tag className="pc-heading-text">{children}</Tag>
      {meta ? <span className="pc-heading-meta">{meta}</span> : null}
    </div>
  );
}

export type SeriesKey = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 'ink';

export interface LegendItem {
  name: string;
  series: SeriesKey;
}

/** Series names in their fixed colours, plus the backfill marker when part of
 *  the history was filled in from a named source. */
export function Legend({ items, backfill }: { items: readonly LegendItem[]; backfill?: string }) {
  return (
    <ul data-slot="content-legend" className="pc-legend">
      {items.map((item) => (
        <li key={item.name}>
          <i aria-hidden="true" data-series={item.series} />
          {item.name}
        </li>
      ))}
      {backfill ? (
        <li data-backfill="true">
          <i aria-hidden="true" />
          {backfill}
        </li>
      ) : null}
    </ul>
  );
}

/** A chart in a panel: title, meta, legend, then the renderer's drawing. */
export function ChartFrame({
  title,
  meta,
  legend,
  backfill,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  legend?: readonly LegendItem[];
  backfill?: string;
  children: ReactNode;
}) {
  return (
    <Panel title={title} meta={meta}>
      {legend && legend.length ? <Legend items={legend} backfill={backfill} /> : null}
      <div data-slot="content-chart" className="pc-chart-slot">
        {children}
      </div>
    </Panel>
  );
}

/** Label and value pairs: a source's details, a record's fields. */
export function KeyValueList({ items }: { items: readonly { label: string; value: ReactNode }[] }) {
  return (
    <dl data-slot="content-kv" className="pc-kv">
      {items.map((item) => (
        <div key={item.label} className="pc-kv-row">
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
